import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { MessageSquarePlus, Send } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  ["bug", "Bug"], ["confusing_step", "Confusing Step"], ["missing_information", "Missing Information"],
  ["unnecessary_step", "Unnecessary Step"], ["workflow_problem", "Workflow Problem"], ["time_efficiency", "Time Efficiency"],
  ["app_usefulness", "App Usefulness/Usability"], ["equipment_issue", "Equipment/Workflow Issue"],
  ["shadow_scan", "Shadow Scan Usefulness"], ["estimator", "Estimator Usefulness"],
  ["safety_training", "Safety/Training Concern"], ["suggested_improvement", "Suggested Improvement"], ["other", "Other"],
];
const CAT_LABEL = Object.fromEntries(CATEGORIES);
const STATUSES = ["new", "reviewing", "resolved", "deferred"];
const STATUS_MAP = { new: "info", reviewing: "warning", resolved: "ready", deferred: "unpaid" };

export default function Feedback() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ category: "bug", description: "", context: "" });

  const load = async () => { const { data } = await api.get("/feedback"); setItems(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Description required"); return; }
    try { await api.post("/feedback", form); toast.success("Feedback submitted"); setForm({ category: "bug", description: "", context: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const dispose = async (fb, status) => {
    const note = window.prompt(`Set status to ${status.toUpperCase()}. Note (optional):`, fb.disposition_note || "");
    if (note === null) return;
    try { await api.patch(`/feedback/${fb.id}`, { status, note }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!items) return <Loading label="Loading feedback" />;

  return (
    <div className="max-w-4xl">
      <SectionTitle sub="Report bugs, confusing steps and field observations during controlled testing">Field QA Feedback</SectionTitle>

      <div className="sg-card p-5 mb-6" data-testid="feedback-form">
        <div className="font-head font-bold mb-3 flex items-center gap-2"><MessageSquarePlus size={18} /> Submit Feedback</div>
        <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Category</label>
        <select className="sg-input mt-1 mb-3" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="feedback-category">
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Description</label>
        <textarea className="sg-input mt-1 mb-3" rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="feedback-description" placeholder="What happened? What would make it better?" />
        <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Context (screen / job / step — optional)</label>
        <input className="sg-input mt-1 mb-4" value={form.context} onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))} data-testid="feedback-context" placeholder="e.g. Shadow Scan · Access step" />
        <button className="sg-btn sg-btn-accent" onClick={submit} data-testid="feedback-submit"><Send size={16} /> Submit</button>
      </div>

      <div className="font-head font-bold mb-3">{isOwner || user.role === "manager" ? "All Feedback" : "My Feedback"}</div>
      <div className="flex flex-col gap-3" data-testid="feedback-list">
        {items.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No feedback yet.</div>}
        {items.map((fb) => (
          <div key={fb.id} className="sg-card p-4" data-testid={`feedback-${fb.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="chip" style={{ background: "var(--bg-elev)", color: "var(--txt2)", borderColor: "var(--brd)" }}>{CAT_LABEL[fb.category] || fb.category}</span>
                <StatusChip status={STATUS_MAP[fb.status]} label={fb.status} />
              </div>
              <div className="text-xs" style={{ color: "var(--txt2)" }}>{fb.user_name} ({fb.role}) · {new Date(fb.created_at).toLocaleString()}</div>
            </div>
            <div className="text-sm mt-2">{fb.description}</div>
            {fb.context && <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>Context: {fb.context}</div>}
            {fb.disposition_by && <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>Owner: {fb.status} by {fb.disposition_by}{fb.disposition_note ? ` — “${fb.disposition_note}”` : ""}</div>}
            {isOwner && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {STATUSES.filter((s) => s !== "new").map((s) => (
                  <button key={s} className="sg-btn" style={{ minHeight: 34, padding: "4px 12px" }} onClick={() => dispose(fb, s)} data-testid={`feedback-set-${s}-${fb.id}`}>{s}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
